import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../layout/AdminLayout';
import { PageHeader } from '../common/PageHeader';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { seminarApi } from '../../lib/api';
import { formatCurrency, isSeminarOver } from '../../lib/utils';
import { toast } from 'sonner';
import { Plus, Loader2, Search, Users, Tag, Copy } from 'lucide-react';

const EVENT_CATEGORY_LABELS = {
    fortbildung: 'Fortbildung',
    weiterbildung: 'Weiterbildung',
    ausbildung: 'Ausbildung',
};

const EVENT_FORMAT_LABELS = {
    praesenz: { label: 'Präsenz' },
    hybrid: { label: 'Hybrid' },
    online: { label: 'Online' },
};

export function SeminarsPage() {
    const [seminars, setSeminars] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filter, setFilter] = useState('current');
    const navigate = useNavigate();

    useEffect(() => { loadSeminars(); }, []);

    const loadSeminars = async () => {
        try {
            const response = await seminarApi.getAll();
            setSeminars(response.data);
        } catch {
            toast.error('Fehler beim Laden der Seminare');
        } finally {
            setLoading(false);
        }
    };

    const handleDuplicate = async (e, seminar) => {
        e.stopPropagation();
        try {
            // Get first date year
            const dates = seminar.dates?.length ? seminar.dates : [];
            const firstDate = dates[0]?.date;
            const year = firstDate && firstDate.includes('-') ? parseInt(firstDate.split('-')[0]) : undefined;
            const res = await seminarApi.nextNumber(year);
            const nextNum = res.data.course_number;

            const payload = {
                title: seminar.title || '',
                subtitle: seminar.subtitle || '',
                course_number: seminar.organizer_type === 'bgpr' ? nextNum : '',
                description: seminar.description || '',
                dates: [{ date: '', time_start: '09:00', time_end: '17:00' }],
                date: '', time_start: '09:00', time_end: '17:00',
                location: seminar.location || '',
                instructor_name: seminar.instructor_name || '',
                ue_count: seminar.ue_count || 8,
                max_participants: seminar.max_participants || 20,
                min_participants: seminar.min_participants || 5,
                price: seminar.price || 0,
                discounts: (seminar.discounts || []).map(d => ({ ...d, valid_from: '', valid_until: '' })),
                external_link: seminar.external_link || '',
                organizer: seminar.organizer || 'BGPR-Akademie',
                organizer_type: seminar.organizer_type || 'bgpr',
                organizer_name: seminar.organizer_name || '',
                event_category: seminar.event_category || 'fortbildung',
                event_format: seminar.event_format || 'praesenz',
                certificate_content: seminar.certificate_content || '',
                is_published: false,
            };
            const created = await seminarApi.create(payload);
            toast.success('Kopie erstellt');
            navigate(`/admin/seminars/${created.data.id}`);
        } catch {
            toast.error('Fehler beim Kopieren');
        }
    };

    const filteredSeminars = seminars.filter((s) => {
        const matchesSearch = s.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            s.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (s.course_number || '').toLowerCase().includes(searchTerm.toLowerCase());
        if (!matchesSearch) return false;
        if (filter === 'all') return true;

        // Symmetrie zum Backend (_seminar_all_dates_past): inkl. End-Uhrzeit am letzten Termin.
        const isExpired = isSeminarOver(s);
        if (filter === 'current') return !isExpired;
        if (filter === 'expired') return isExpired;
        return true;
    });

    return (
        <AdminLayout>
            <PageHeader
                title="Fortbildungen"
                description="Verwalten Sie Seminare und Fortbildungsveranstaltungen"
                actions={
                    <Button onClick={() => navigate('/admin/seminars/new')} data-testid="add-seminar-button">
                        <Plus className="mr-2 h-4 w-4" />
                        Neue Fortbildung
                    </Button>
                }
            />

            <div className="mb-6 flex items-center gap-4 flex-wrap">
                <div className="relative max-w-sm flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Suchen (Titel, Ort, Kursnummer)..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9"
                        data-testid="search-seminars"
                    />
                </div>
                <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-1">
                    {[
                        { value: 'current', label: 'Aktuelle' },
                        { value: 'expired', label: 'Abgelaufen' },
                        { value: 'all', label: 'Alle' },
                    ].map((f) => (
                        <button
                            key={f.value}
                            onClick={() => setFilter(f.value)}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                                filter === f.value
                                    ? 'bg-white text-foreground shadow-sm'
                                    : 'text-muted-foreground hover:text-foreground'
                            }`}
                            data-testid={`filter-${f.value}`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            ) : filteredSeminars.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground">
                    <p>Keine Fortbildungen gefunden</p>
                </div>
            ) : (
                <div className="border rounded-lg overflow-hidden bg-white">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b bg-muted/30">
                                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider text-muted-foreground">Fortbildung</th>
                                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider text-muted-foreground">Termine & Ort</th>
                                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider text-muted-foreground">UE / Preis</th>
                                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider text-muted-foreground">Teilnehmer</th>
                                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider text-muted-foreground">Status</th>
                                <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider text-muted-foreground"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredSeminars.map((row) => {
                                const dates = row.dates?.length
                                    ? row.dates
                                    : (row.date ? [{ date: row.date, time_start: row.time_start, time_end: row.time_end }] : []);
                                const isExternal = (row.organizer_type || 'bgpr') === 'external';
                                return (
                                    <tr
                                        key={row.id}
                                        className={`border-b last:border-b-0 hover:bg-muted/20 cursor-pointer transition-colors border-l-[3px] ${isExternal ? 'border-l-[#E5007D]' : 'border-l-primary'}`}
                                        onClick={() => navigate(`/admin/seminars/${row.id}`)}
                                        data-testid={`seminar-row-${row.id}`}
                                    >
                                        <td className="px-4 py-3">
                                            <div>
                                                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                                                    <p className="font-medium" data-testid={`seminar-row-title-${row.id}`}>{row.title}</p>
                                                    {row.course_number && (
                                                        <span className="text-xs text-muted-foreground font-mono">{row.course_number}</span>
                                                    )}
                                                    {row.is_in_house && (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-md border bg-primary/10 text-primary border-primary/30 uppercase tracking-wide" data-testid={`seminar-badge-in-house-${row.id}`} title={`In-House für ${row.in_house_company?.name || 'Firma'}`}>
                                                            In-House
                                                        </span>
                                                    )}
                                                    {row.is_cancelled && (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-md border bg-red-50 text-red-800 border-red-200 uppercase tracking-wide" data-testid={`seminar-badge-cancelled-${row.id}`}>
                                                            Abgesagt
                                                        </span>
                                                    )}
                                                </div>
                                                {row.subtitle && <p className="text-xs text-muted-foreground mb-0.5">{row.subtitle}</p>}
                                                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-1">
                                                    {!isExternal ? 'BGPR-Akademie' : (row.organizer_name || row.organizer || 'Extern')}
                                                    {!isExternal && row.event_category && (
                                                        <> · {EVENT_CATEGORY_LABELS[row.event_category] || 'Fortbildung'}</>
                                                    )}
                                                    {!isExternal && row.event_format && row.event_format !== 'praesenz' && (
                                                        <> · {(EVENT_FORMAT_LABELS[row.event_format] || EVENT_FORMAT_LABELS.praesenz).label}</>
                                                    )}
                                                </p>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            {dates.slice(0, 3).map((d, i) => (
                                                <div key={i} className="flex items-center gap-1.5 text-sm">
                                                    <span className="font-medium">{d.date}</span>
                                                    <span className="text-muted-foreground text-xs">{d.time_start}&ndash;{d.time_end}</span>
                                                </div>
                                            ))}
                                            {dates.length > 3 && <span className="text-xs text-muted-foreground">+{dates.length - 3} weitere</span>}
                                            {dates.length === 0 && <span className="text-xs text-muted-foreground">Kein Termin</span>}
                                            <p className="text-sm text-muted-foreground mt-0.5">{row.location}</p>
                                        </td>
                                        <td className="px-4 py-3">
                                            <p className="font-medium">{row.ue_count} UE</p>
                                            <p className="text-sm text-muted-foreground">{formatCurrency(row.price)}</p>
                                            {row.discounts?.length > 0 && (
                                                <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                                                    <Tag className="h-3 w-3" />
                                                    {row.discounts.length} Rabatt{row.discounts.length > 1 ? 'e' : ''}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <Users className="h-4 w-4 text-muted-foreground" />
                                                <span className={row.current_participants >= row.min_participants ? 'text-emerald-600' : ''}>
                                                    {row.current_participants}/{row.max_participants}
                                                </span>
                                                {row.current_participants < row.min_participants && (
                                                    <span className="text-xs text-amber-600">(min. {row.min_participants})</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                                                row.is_published ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'
                                            }`}>
                                                <span className={`w-1.5 h-1.5 rounded-full ${row.is_published ? 'bg-green-500' : 'bg-gray-400'}`} />
                                                {row.is_published ? 'Online' : 'Offline'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <Button
                                                variant="ghost" size="sm"
                                                onClick={(e) => handleDuplicate(e, row)}
                                                title="Kopieren"
                                                data-testid={`duplicate-seminar-${row.id}`}
                                            >
                                                <Copy className="h-4 w-4" />
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </AdminLayout>
    );
}
